import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { canWrite, WRITE_PERMISSION_DENIED } from '@/services/permissions';
import { recordAudit } from '@/services/audit';

export interface Salesperson {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  commissionRate: number;
  isActive: boolean;
  createdAt: string;
}

export interface SalespersonsResult {
  data: Salesperson[];
}

export async function fetchSalespersons(): Promise<SalespersonsResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { data: [] };
  }

  try {
    const { data, error } = await supabase.from('salespersons').select('*');

    if (error) {
      throw new Error(error.message);
    }

    const mapped: Salesperson[] = ((data ?? []) as Array<Record<string, any>>)
      .filter((r) => r.is_active !== false)
      .map((r) => ({
        id: r.id,
        fullName: r.full_name,
        email: r.email ?? '',
        phone: r.phone ?? '',
        commissionRate: Number(r.commission_rate ?? 0),
        isActive: r.is_active ?? true,
        createdAt: r.created_at ?? '',
      }))
      .sort((a, b) => (a.fullName < b.fullName ? -1 : 1));

    return { data: mapped };
  } catch (err) {
    console.error('[salespersons] connection failed:', err);
    throw err;
  }
}

export interface SalespersonInput {
  fullName: string;
  email: string;
  phone: string;
  commissionRate: number;
  isActive: boolean;
}

export async function saveSalesperson(
  input: SalespersonInput,
): Promise<{ data: Salesperson | null; error: string | null }> {
  if (!(await canWrite('salespersons'))) {
    return { data: null, error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { data: null, error: 'Supabase no está configurado.' };
  }

  const payload = {
    full_name: input.fullName.trim(),
    email: input.email.trim() || null,
    phone: input.phone.trim() || null,
    commission_rate: Number(input.commissionRate) || 0,
    is_active: input.isActive,
  };

  try {
    const { data, error } = await supabase.from('salespersons').insert(payload).select('*');
    if (error) return { data: null, error: error.message };
    const row = data?.[0] as Record<string, any> | undefined;
    if (!row) return { data: null, error: 'No se pudo leer el vendedor guardado.' };
    recordAudit({
      entityType: 'salespersons',
      entityId: row.id,
      action: 'CREATE',
      newValue: input.fullName,
    });
    return {
      data: {
        id: row.id,
        fullName: row.full_name,
        email: row.email ?? '',
        phone: row.phone ?? '',
        commissionRate: Number(row.commission_rate ?? 0),
        isActive: row.is_active ?? true,
        createdAt: row.created_at ?? '',
      },
      error: null,
    };
  } catch (err) {
    console.warn('[salespersons] save failed:', err);
    return { data: null, error: 'Error de conexión al guardar el vendedor.' };
  }
}

// Alterna el estado activo de un vendedor.
export async function toggleSalesperson(
  id: string,
  isActive: boolean,
): Promise<{ error: string | null }> {
  if (!(await canWrite('salespersons'))) {
    return { error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { error: 'Supabase no está configurado.' };
  }

  try {
    const { error } = await supabase.from('salespersons').update({ is_active: isActive }).eq('id', id);
    if (!error) {
      recordAudit({
        entityType: 'salespersons',
        entityId: id,
        action: 'UPDATE',
        fieldName: 'is_active',
        oldValue: String(!isActive),
        newValue: String(isActive),
      });
    }
    return { error: error?.message ?? null };
  } catch (err) {
    console.warn('[salespersons] toggle failed:', err);
    return { error: 'Error de conexión al actualizar el vendedor.' };
  }
}